import type { Entity } from './api.js';
import { readNote } from './notes.js';
import { urlFor } from './thumbs.js';

/**
 * **Envoyer vers une autre application** — la feuille de partage du système,
 * par `navigator.share()` *(N6, 2026-08-18)*.
 *
 * ### Pourquoi « Envoyer » et jamais « Partager »
 *
 * Dans Synkara, **« partage » est déjà un mot**, et il désigne le partage à un
 * cercle : `vocabulaire.spec.ts` impose que `collection.title` vaille
 * « Partage », et [`menu.tsx`](ui/menu.tsx) explique en toutes lettres pourquoi
 * son menu n'offre pas de « Partager ». Une entrée qui porterait ce mot pour
 * dire « envoyer les octets à WhatsApp » ferait dire deux choses au même mot :
 * exposer à quelqu'un, et sortir de l'application. Le geste sort d'ici ; le
 * verbe le dit.
 *
 * ### Ce qui sort, et sous quelle forme
 *
 * - Une **photo ou une vidéo** part en `standard` — le dérivé, jamais la source.
 *   La source dort en Glacier IR après 30 jours (`roles.ts`), donc l'envoyer
 *   ferait payer une récupération sur un geste qu'on attend immédiat.
 * - Une **note** part en `{title, text}`, sans fichier. C'est le niveau 1 de
 *   l'API, celui que tous les navigateurs qui font quelque chose savent faire, et
 *   il **n'a besoin d'aucun octet** : pas de fichier à construire, donc pas de
 *   course contre l'activation (voir plus bas). Un `.md` aurait été plus fragile
 *   — la liste des types de fichiers acceptés est laissée **à chaque navigateur**
 *   par la spec, et le Markdown ne figure sur aucune de celles qui sont publiées.
 *
 * ### Le piège : l'activation utilisateur est **consommée**, et elle est courte
 *
 * La spec l'exige et l'épuise — « If |global| does not have transient
 * activation, return a promise rejected with a `NotAllowedError` », puis
 * « Consume user activation ». Tout `await` entre le clic et `navigator.share()`
 * peut donc faire échouer l'envoi, et Safari iOS est le strict de la famille.
 *
 * Trois choses le tiennent, et la deuxième est la seule qui garantisse quelque
 * chose :
 *
 * ① **On ne prépare pas à l'ouverture du menu.** Ce serait la parade évidente,
 *    et elle est fausse : ouvrir un « … » sur une vidéo de 40 Mo téléchargerait
 *    40 Mo **parce qu'on a ouvert un menu**, sur un forfait mobile.
 * ② **Le colis est gardé après un échec** (`derniere`), si bien qu'un second
 *    clic appelle `navigator.share()` **sans aucun `await`** : il ne peut plus
 *    manquer l'activation. Le repli est donc « réessayez », et il se répare
 *    lui-même au lieu de renvoyer l'utilisateur nulle part.
 * ③ **L'entrée ne vit que là où les octets sont déjà chauds** — la visionneuse et
 *    le lecteur —, où `urlFor` rend l'URL présignée depuis son cache et où le
 *    navigateur a souvent déjà les octets. Le premier clic suffit alors.
 */

/**
 * **Le plafond, et pourquoi il n'est pas 50 Mo.**
 *
 * Chromium refuse un envoi de plus de 10 fichiers ou **50 Mo au total**. On
 * n'envoie qu'un fichier, donc tout le budget lui revient — mais 50 Mo décimaux
 * font moins que 50 Mio, et se poser exactement sur une limite qu'on ne mesure
 * pas est le meilleur moyen de la franchir. La marge est ce qui reste vrai dans
 * les deux unités.
 *
 * Au-delà, **l'entrée n'apparaît pas** : c'est la règle de la maison — un
 * contrôle désactivé annonce une fonction qui n'existe pas —, et « Télécharger »
 * est le chemin qui reste, sans plafond.
 */
export const SEND_MAX_BYTES = 45 * 1024 * 1024;

/** Ce qu'un objet sait envoyer, **décidé avant le clic**, sans lire un octet. */
export type Sendable =
  | { readonly kind: 'note' }
  | { readonly kind: 'media'; readonly mime: string; readonly ext: string; readonly size: number };

/**
 * **La décision, pure.** Elle ne consulte que la fiche : c'est ce qui permet à
 * l'entrée d'apparaître ou non **au rendu**, sans rien demander au réseau.
 */
export function sendableOf(entity: Entity): Sendable | undefined {
  if (entity.Type === 'NOTE') {
    // Le drapeau est `size`, jamais `State` : une note créée et jamais écrite n'a
    // aucun octet, et il n'y a alors rien à envoyer — pas même son titre seul.
    return entity.Variants?.['content']?.size === undefined ? undefined : { kind: 'note' };
  }
  const standard = entity.Variants?.['standard'];
  // `size` absent veut dire « pas encore » : le dérivé est en cours de calcul.
  if (standard?.size === undefined) return undefined;
  if (standard.size > SEND_MAX_BYTES) return undefined;
  return { kind: 'media', mime: standard.mime, ext: standard.ext, size: standard.size };
}

/**
 * **Ce que le navigateur sait faire**, et les deux niveaux ne se confondent pas :
 * `share()` existe depuis longtemps partout sauf Firefox bureau, mais l'envoi de
 * **fichiers** est le niveau 2 — Firefox ne l'a nulle part, et `canShare` est le
 * seul marqueur qui le distingue.
 */
export function browserCanSend(kind: Sendable['kind']): boolean {
  if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') return false;
  return kind === 'note' || typeof navigator.canShare === 'function';
}

/**
 * **Le nom du fichier envoyé porte l'extension de ce qu'il contient.**
 *
 * Une photo s'appelle `IMG_1234.jpg` et son dérivé `standard` est un **WebP** :
 * l'envoyer sous son nom d'origine donnerait à l'application réceptrice un
 * fichier qui ment sur son type, et beaucoup décident par l'extension. Ici la
 * question se tranche — contrairement au téléchargement, où renommer changerait
 * le nom de fichiers que les gens ont déjà.
 */
export const sendName = (name: string, ext: string): string =>
  `${name.replace(/\.[A-Za-z0-9]{1,8}$/, '').trim() || 'fichier'}.${ext}`;

/**
 * L'issue d'une préparation.
 *
 * **Quatre cas et non deux** *(2026-08-18)*. La première version les repliait sur
 * « échoué », et c'était une faute de conception : un seul message pour quatre
 * causes ne se diagnostique pas — l'utilisateur lit « l'envoi a échoué » et
 * personne, lui compris, ne sait de quelle moitié du chemin il parle. Chaque
 * refus porte donc désormais **ce qui a refusé**.
 */
export type Parcel =
  | { readonly ok: true; readonly data: ShareData }
  /** Le navigateur refuse ce fichier — type, taille, ou niveau 2 absent. */
  | { readonly ok: false; readonly reason: 'unsupported' }
  /** Les octets ne sont pas venus : le `status` est ce qu'on demandera à relire. */
  | { readonly ok: false; readonly reason: 'bytes'; readonly status: number }
  /** Tout le reste, **avec le nom de l'erreur** : un code se recopie, pas une phrase. */
  | { readonly ok: false; readonly reason: 'failed'; readonly code: string };

/**
 * **Ce que le navigateur accepte, testé sur l'objet qu'on va réellement lui
 * passer.**
 *
 * La première version validait `{files}` puis envoyait `{files, title}` : deux
 * objets différents, donc une validation qui ne validait pas ce qui partait. MDN
 * le dit en une ligne — « canShare() supports only the properties you intend to
 * share » —, et le rejet qui s'ensuit est un `TypeError`, c'est-à-dire
 * précisément le cas que l'ancienne classification ne savait pas nommer.
 *
 * `required` distingue les deux niveaux : sans `canShare`, un navigateur peut
 * encore partager du texte (c'est Firefox Android), mais **jamais** un fichier.
 */
export const accepts = (data: ShareData, required: boolean): boolean =>
  typeof navigator.canShare === 'function' ? navigator.canShare(data) : !required;

/**
 * **Le colis du dernier envoi**, gardé pour qu'un second clic n'ait plus rien à
 * attendre. Un seul cran : garder plus retiendrait des mégaoctets en mémoire
 * pour des objets qu'on ne renverra pas.
 */
let derniere: { fid: string; parcel: Parcel } | undefined;

/** Ce qui est déjà prêt pour cet objet, s'il y a quelque chose. */
export const readyFor = (fid: string): Parcel | undefined =>
  derniere?.fid === fid ? derniere.parcel : undefined;

/** Oublie le colis — la fiche a changé sous lui. */
export const forgetParcel = (): void => void (derniere = undefined);

export async function prepare(entity: Entity, what: Sendable): Promise<Parcel> {
  const fid = entity.Fid ?? '';
  const parcel = await build(entity, what);
  if (parcel.ok) derniere = { fid, parcel };
  return parcel;
}

async function build(entity: Entity, what: Sendable): Promise<Parcel> {
  try {
    if (what.kind === 'note') {
      const text = await readNote(entity);
      if (text.trim() === '') return { ok: false, reason: 'failed', code: 'empty' };
      const data: ShareData = { title: entity.Name, text };
      return accepts(data, false) ? { ok: true, data } : { ok: false, reason: 'unsupported' };
    }

    // `urlFor` rend l'URL depuis son cache quand l'objet est affiché : c'est ce
    // qui fait qu'il ne reste **qu'un** saut réseau après le clic, et souvent
    // aucun, les octets étant déjà dans le cache du navigateur.
    const url = await urlFor(entity.Fid ?? '', 'standard');
    const res = await fetchBytes(url);
    if (!res.ok) return { ok: false, reason: 'bytes', status: res.status };
    const file = new File([await res.blob()], sendName(entity.Name, what.ext), {
      type: what.mime,
    });
    // **Un fichier part seul** *(2026-08-18)*. La première version joignait
    // `title` : avec des fichiers, la plupart des destinations l'ignorent — le
    // nom du fichier porte déjà le nom —, et plusieurs implémentations refusent
    // la combinaison par un `TypeError`. Ce qu'on gagnait était nul, ce qu'on
    // risquait était l'échec entier.
    const data: ShareData = { files: [file] };
    // **Testé sur l'objet qui partira**, et non sur un autre qui lui ressemble.
    if (!accepts(data, true)) return { ok: false, reason: 'unsupported' };
    return { ok: true, data };
  } catch (err) {
    // Le nom de ce qui a été levé — `TypeError`, `NotReadableError`, autre —
    // vaut mieux que le silence : c'est ce qu'on demandera de recopier.
    return { ok: false, reason: 'failed', code: err instanceof Error ? err.name : 'Error' };
  }
}

/**
 * **La miniature du dernier lien préparé** *(L1, 2026-09-14)* — un cran, comme
 * `derniere`. Elle est gardée **sans l'adresse** : le partage visé se choisit
 * parfois après, et le colis se compose alors sans aucun `await`.
 */
let vignette: { fid: string; file: File } | undefined;

/** La miniature déjà prête pour cet objet, s'il y en a une. */
export const thumbFor = (fid: string): File | undefined =>
  vignette?.fid === fid ? vignette.file : undefined;

/**
 * **Chercher la miniature**, à l'ouverture du menu.
 *
 * Ce que `standard` interdit — préparer avant le clic ferait télécharger une
 * vidéo de 40 Mo parce qu'on a ouvert un menu —, ~20 Ko le permettent. C'est ce
 * qui fait partir **le premier clic** : l'activation utilisateur n'attend plus
 * rien. Un échec ici n'est rien ; le clic retentera et dira ce qui a refusé.
 */
export async function prepareThumb(
  entity: Entity,
  what: { mime: string; ext: string },
): Promise<{ ok: true; file: File } | Exclude<Parcel, { ok: true }>> {
  const fid = entity.Fid ?? '';
  const ready = thumbFor(fid);
  if (ready) return { ok: true, file: ready };
  try {
    const res = await fetchBytes(await urlFor(fid, 'thumb'));
    if (!res.ok) return { ok: false, reason: 'bytes', status: res.status };
    const file = new File([await res.blob()], sendName(entity.Name, what.ext), {
      type: what.mime,
    });
    vignette = { fid, file };
    return { ok: true, file };
  } catch (err) {
    return { ok: false, reason: 'failed', code: err instanceof Error ? err.name : 'Error' };
  }
}

/**
 * **Le colis d'un lien** : la miniature, et l'adresse **dans `text`**.
 *
 * Pas dans `url` : avec un fichier, plusieurs destinations gardent le fichier et
 * jettent l'URL, alors que le texte devient la légende. Synchrone, et testé sur
 * l'objet qui partira.
 */
export function linkParcel(file: File, url: string): Parcel {
  const data: ShareData = { files: [file], text: url };
  return accepts(data, true) ? { ok: true, data } : { ok: false, reason: 'unsupported' };
}

/**
 * **Les octets, en se méfiant du cache du navigateur** *(2026-08-18)*.
 *
 * Une image affichée par `<img src>` sans `crossorigin` est demandée **sans
 * en-tête `Origin`** : S3 répond alors sans `Access-Control-Allow-Origin`, et le
 * navigateur met cette réponse en cache telle quelle. Un `fetch()` de la **même
 * URL** est, lui, une requête CORS : il retrouve l'entrée dépourvue de l'en-tête,
 * la refuse, et rejette par un **`TypeError`** — le rejet le plus trompeur qui
 * soit, puisque l'image est sous les yeux au moment où « les octets ne peuvent
 * pas être récupérés ».
 *
 * La cause est traitée à la source — la visionneuse charge désormais en CORS —,
 * mais **une entrée déjà empoisonnée survit à la correction**, et d'autres écrans
 * peuvent en fabriquer. Le second essai contourne donc le cache : il recoûte les
 * octets, ce qui est très préférable à un geste qui ne marche pas.
 *
 * Seul un `TypeError` déclenche ce repli : c'est la façon dont `fetch` signale un
 * échec réseau ou CORS, et rejouer sur autre chose masquerait une vraie panne.
 */
export async function fetchBytes(url: string): Promise<Response> {
  try {
    return await fetch(url);
  } catch (err) {
    if (!(err instanceof TypeError)) throw err;
    return fetch(url, { cache: 'reload' });
  }
}

/** Ce qu'un envoi peut donner. `cancelled` n'est **pas** une erreur. */
export type SendResult =
  | { readonly kind: 'sent' }
  | { readonly kind: 'cancelled' }
  | { readonly kind: 'expired' }
  | { readonly kind: 'failed'; readonly code: string };

export async function send(data: ShareData): Promise<SendResult> {
  try {
    await navigator.share(data);
    return { kind: 'sent' };
  } catch (err) {
    // **On classe par le `name`, jamais par la classe** *(2026-08-18)*. La
    // première version testait `instanceof DOMException` en premier et rendait
    // « échoué » pour tout le reste — or la spec fait rejeter `share()` avec un
    // **`TypeError`** sur une donnée invalide, qui n'en est pas un, et rien ne
    // garantit qu'un moteur donné rejette avec la classe attendue. Les deux
    // seuls cas qu'il fallait distinguer tombaient donc du mauvais côté.
    const name = err instanceof Error ? err.name : 'Error';
    // **Refermer la feuille est un geste, pas une panne.** `AbortError` couvre
    // aussi « aucune destination disponible » : dans les deux cas l'utilisateur
    // n'a rien à lire, et un bandeau rouge après une annulation volontaire est
    // la façon la plus sûre de faire croire à un bogue.
    if (name === 'AbortError') return { kind: 'cancelled' };
    // L'activation a expiré pendant la préparation. Le colis est gardé : le
    // prochain clic partira sans `await`, donc il aboutira.
    if (name === 'NotAllowedError') return { kind: 'expired' };
    return { kind: 'failed', code: name };
  }
}
